import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useState } from 'react';
import { getErrorMessage } from '@billda/shared-ui';
import { useCreateLabourItem } from '../../labour/hooks/useLabourMutations.ts';
import { addLabourItemSchema } from '../validation/addLabourItemSchema.ts';
import type { AddLabourItemFormData } from '../validation/addLabourItemSchema.ts';

interface UseAddLabourItemFormOptions {
  onSuccess?: () => void;
}

/**
 * Orchestration hook for Add Labour Item form
 * Follows the 9-section pattern from form-architecture.md
 */
export const useAddLabourItemForm = (
  siteId: number,
  plotId: number,
  options?: UseAddLabourItemFormOptions
) => {
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // 1. Routing & URL Parameters
  // (No navigation needed for this form)

  // 2. Data Fetching
  // (No additional queries needed - autocomplete handles its own data)

  // 3. Dynamic Yup Schema
  const validationSchema = addLabourItemSchema;

  // 4. React Hook Form Setup
  const form = useForm<AddLabourItemFormData>({
    resolver: yupResolver(validationSchema) as any,
    defaultValues: {
      selectedDefinition: null,
      name: undefined,
      description: undefined,
      price: 0,
    },
    mode: 'onBlur', // Validate on blur for better UX
  });

  const { reset } = form;

  // 5. Watch Form Values
  // (No watched values needed)

  // 6. Auto-Selection Logic
  // (No auto-fill logic needed)

  // 7. Submission Handling
  const mutation = useCreateLabourItem(siteId, plotId);

  const handleSubmit = async (data: AddLabourItemFormData) => {
    setErrorMessage(null);
    setSuccessMessage(null);

    // Build request based on whether creating new or using existing
    // If name is provided, we're creating a new definition
    // If selectedDefinition is provided, we're using an existing definition
    const request = {
      labourItemDefinitionId: data.name
        ? undefined
        : data.selectedDefinition
          ? parseInt(data.selectedDefinition.id, 10)
          : undefined,
      name: data.name || undefined,
      description: data.description || undefined,
      price: data.price,
    };

    try {
      const response = await mutation.mutateAsync(request);
      const message = response.createdNewDefinition
        ? `Created "${response.definitionName}" and added to plot`
        : `Added "${response.definitionName}" to plot`;
      setSuccessMessage(message);
      setErrorMessage(null);
      reset();
      options?.onSuccess?.();
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
      setSuccessMessage(null);
    }
  };

  // 8. Navigation Handlers
  const handleCancel = () => {
    reset();
    setErrorMessage(null);
    setSuccessMessage(null);
  };

  // 9. Return Orchestrated State
  return {
    // Form instance
    form,

    // Submission
    handleSubmit,
    handleCancel,

    // State
    isSubmitting: mutation.isPending,
    successMessage,
    errorMessage,
  };
};
